import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {Game} from "../Game";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {GameAssets} from "../GameAssets";
import {MyriadPro} from "../Fonts";

export class WinWaysView extends PIXI.Container implements StageLayer {

    private _totalWinWays: PIXI.Text; // this is the number of win ways shown after initial win count up(placed below the MAIN count up shown on screen's centre).
    private _totalWinWaysContainer: PIXI.Container;
    private _toggleWinAmount: PIXI.BitmapText; // this is the win amount shown during toggling.
    private _toggleWinWaysContainer: PIXI.Container;
    private _toggleWinWays: PIXI.Text; // this is the number of win ways shown during toggling.
    private _fadeInToggleTween: gsap.core.Tween;
    private readonly ALPHA_VALUE: number = 0.65;

    constructor() {
        super();
        this.init();
    }

    public showInitialWinWays(): gsap.core.Timeline {
        if (Game.isWinBelowStake()) {
            return gsap.timeline();
        }
        const tl: gsap.core.Timeline = gsap.timeline(),
            betWays: IBetWayWinData[] = Game.model.gameData.betWayWins,
            container: PIXI.Container = new PIXI.Container();

        container.position.set(360);
        container.alpha = 0;

        betWays.forEach((betWay: IBetWayWinData, idx: number) => {
            const winAmount: PIXI.BitmapText = new PIXI.BitmapText(betWay.multipliedAmount.toFixed(2), {
                    fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
                    fontSize: 12
                }),
                waysContainer: PIXI.Container = new PIXI.Container(),
                waysAmount: PIXI.Text = this.createWaysStringText(32);
            waysAmount.text = betWay.numberOfCombinations + " " + this.getWaysWonString(betWay.numberOfCombinations);
            winAmount.y = (idx * 30) + (idx * 80);
            winAmount.pivot.set(winAmount.width / 2, winAmount.height / 2);
            waysContainer.y = (winAmount.y + 20);

            waysContainer.addChild(waysAmount);
            waysContainer.pivot.set(waysContainer.width / 2 - 10, 0);

            container.addChild(waysContainer, winAmount);
        });

        if (betWays.length > 5) {
            container.scale.set(0.65);
        }
        container.y -= (container.height / 2);

        this.addChild(container);

        tl.to(container, {
            duration: 0.1,
            alpha: 1
        });
        tl.to(container, {
            duration: 0.3,
            y: "-=50"
        });
        tl.to(container, {
            duration: 0.1,
            alpha: 0
        });

        tl.add(() => {
            container.removeChildren();
            this.removeChild(container);
        });

        return tl;
    }

    public showTotalWinWaysItems(isBigWin: boolean): void {
        let totalWaysWon: number = 0;
        const betWaysData: IBetWayWinData[] = Game.model.gameData.betWayWins;
        for (let i: number = 0; i < betWaysData.length; i++) {
            totalWaysWon += betWaysData[i].numberOfCombinations;
        }

        this._totalWinWays.text = totalWaysWon + " " + this.getWaysWonString(totalWaysWon);

        this._totalWinWaysContainer.pivot.set(this._totalWinWaysContainer.width / 2, this._totalWinWaysContainer.height / 2);
        if (isBigWin) {
            if (NolimitApplication.isLandscape) {
                this._totalWinWaysContainer.y = 520;
            } else {
                this._totalWinWaysContainer.y = 660;
            }
        } else {
            this._totalWinWaysContainer.y = 440;
        }
        this._totalWinWaysContainer.visible = true;
        this._totalWinWaysContainer.alpha = 1;
    }

    public hideTotalWinWaysItems(duration: number): gsap.core.Tween {
        return gsap.to(this._totalWinWaysContainer, {
            alpha: 0,
            duration: duration,
            onComplete: () => {
                this._totalWinWaysContainer.visible = false;
            }
        });
    }

    public showToggleWinWaysItems(winAmount: number, ways: number): void {
        if (this._totalWinWaysContainer.visible) {
            this._totalWinWaysContainer.visible = false;
        }
        this._toggleWinAmount.scale.set(1);
        this._toggleWinAmount.text = winAmount.toFixed(2);
        this._toggleWinAmount.pivot.set(this._toggleWinAmount.width / 2, this._toggleWinAmount.height / 2);
        this.setScaleToFitMaxWidth(); //#419
        if (ways === 0) {
            this._toggleWinWays.text = " ";
        } else {
            this._toggleWinWays.text = ways + " " + this.getWaysWonString(ways);
        }

        this._toggleWinWaysContainer.pivot.set(this._toggleWinWaysContainer.width / 2, this._toggleWinWaysContainer.height / 2);
        if (!this._toggleWinWaysContainer.visible) {
            this._toggleWinAmount.visible = true;
            this._toggleWinWaysContainer.visible = true;
            this._fadeInToggleTween = gsap.to([this._toggleWinAmount, this._toggleWinWaysContainer], {
                alpha: 1,
                duration: 0.3
            });
        }
    }

    public fadeOutToggleWinWaysItems(): gsap.core.Tween {
        return gsap.to([this._toggleWinAmount, this._toggleWinWaysContainer], {
            alpha: 0,
            duration: 0.6,
            onComplete: () => this.removeToggleWinWaysItems()
        });
    }

    public removeToggleWinWaysItems(): void {
        if (this._fadeInToggleTween) {
            this._fadeInToggleTween.kill();
        }
        this._toggleWinAmount.visible = false;
        this._toggleWinWaysContainer.visible = false;
    }

    private getWaysWonString(noOfWays: number): string {
        return Game.apiPlugin.translations.translate(noOfWays === 1 ? "Way Won" : "Ways Won");
    }

    private getWaysWonTexture(numWaysWon: number): PIXI.Texture {
        return (numWaysWon === 1) ? Game.assetLoader.getTextures("xways_text_image/way")[0] : Game.assetLoader.getTextures("xways_text_image/ways")[0];
    }

    private init(): void {
        this.createTotalWinWaysItems();
        this.createToggleWinWaysItems();
    }

    private createTotalWinWaysItems(): void {
        this._totalWinWaysContainer = new PIXI.Container();
        this._totalWinWaysContainer.visible = false;
        this._totalWinWaysContainer.pivot.set(this._totalWinWaysContainer.width / 2, this._totalWinWaysContainer.height / 2);
        this._totalWinWaysContainer.position.set(360, 440);
        this._totalWinWays = this.createWaysStringText(48);

        this._totalWinWaysContainer.addChild(this._totalWinWays);
        this.addChild(this._totalWinWaysContainer);
    }

    private createWaysStringText(fontSize?: number): PIXI.Text {
        const text: PIXI.Text = new PIXI.Text("", {
            fontFamily: MyriadPro.FAMILY,
            "dropShadow": true,
            "dropShadowBlur": 5,
            "dropShadowDistance": 2,
            "fill": "#ff6600",
            "fontSize": fontSize || 40,
            "lineJoin": "round",
            "miterLimit": 30,
            "strokeThickness": 4
        });
        text.alpha = this.ALPHA_VALUE;
        return text;
    }

    private createToggleWinWaysItems(): void {
        this._toggleWinWaysContainer = new PIXI.Container();
        this._toggleWinWaysContainer.visible = false;
        this._toggleWinWaysContainer.pivot.set(this._toggleWinWaysContainer.width / 2, this._toggleWinWaysContainer.height / 2);
        this._toggleWinWaysContainer.position.set(360, 400);

        this._toggleWinWays = this.createWaysStringText();

        this._toggleWinWaysContainer.addChild(this._toggleWinWays);

        this._toggleWinAmount = new PIXI.BitmapText("", {
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 20
        });
        this._toggleWinAmount.name = "ToggleWinAmount";
        this._toggleWinAmount.visible = false;
        this._toggleWinAmount.position.set(360, 320);

        this.addChild(this._toggleWinWaysContainer, this._toggleWinAmount);
    }

    private setScaleToFitMaxWidth(): void {
        this._toggleWinAmount.updateTransform();
        let textScale: number = 1;
        if (this._toggleWinAmount.width > 600) {
            textScale = Math.min(600 / this._toggleWinAmount.width, 1);
        }
        this._toggleWinAmount.scale.set(textScale);
    }
}